import React, { useState, useEffect } from 'react';
import { retainedEarningsApi } from '../services/api';
import CalculateREModal from './CalculateREModal';
import '../styles/Panel.css';

/**
 * Panel for managing Retained Earnings adjustments
 *
 * Features:
 * - View calculation history
 * - Calculate new adjustments for annual reports
 * - Download generated workbooks
 */
function RetainedEarningsPanel({ entity, onComplete }) {
  const [calculations, setCalculations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showCalculateModal, setShowCalculateModal] = useState(false);
  const [selectedFiscalYearEnd, setSelectedFiscalYearEnd] = useState(''); // For filtering
  const [downloadingTimestamp, setDownloadingTimestamp] = useState(null);

  useEffect(() => {
    if (entity) {
      fetchCalculations();
    }
  }, [entity]);

  const fetchCalculations = async () => {
    if (!entity) return;

    try {
      setLoading(true);
      setError(null);
      const data = await retainedEarningsApi.getHistory(entity.name, selectedFiscalYearEnd || null);
      setCalculations(data.calculations || []);
    } catch (err) {
      console.error('Error fetching calculations:', err);
      setError(
        err.response?.data?.error ||
        err.message ||
        'Failed to load calculation history'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleCalculateComplete = (result) => {
    // Refresh the calculations list
    fetchCalculations();

    if (onComplete) {
      onComplete(result);
    }
  };

  const handleDownload = async (fiscalYearEnd, timestamp) => {
    try {
      setDownloadingTimestamp(timestamp);
      const blob = await retainedEarningsApi.downloadWorkbook(entity.name, fiscalYearEnd, timestamp);

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `retained_earnings_${entity.name}_${fiscalYearEnd}_${timestamp}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error downloading workbook:', err);
      setError('Failed to download workbook');
    } finally {
      setDownloadingTimestamp(null);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    } catch {
      return dateString;
    }
  };

  const formatNumber = (value) => {
    if (value === null || value === undefined) return 'N/A';
    return parseFloat(value).toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  if (!entity) {
    return (
      <div className="panel">
        <h2>Retained Earnings Adjustments</h2>
        <p className="info-text">Please select an entity to view retained earnings calculations.</p>
      </div>
    );
  }

  return (
    <div className="panel">
      <h2>Retained Earnings Adjustments</h2>
      <p className="info-text">
        Entity: <strong>{entity.description || entity.name}</strong>
      </p>

      {/* Info Box */}
      <div className="alert alert-info" style={{ marginBottom: '1.5rem' }}>
        <strong>Schedule III & IndAS Compliant Retained Earnings Adjustments:</strong>
        <p style={{ marginTop: '0.5rem', marginBottom: 0 }}>
          This module calculates retained earnings adjustments for companies transitioning from calendar year (Jan-Dec)
          to fiscal year (Apr-Mar) accounting periods. The system automatically calculates the stub period (Jan-Mar),
          fetches trial balance data with S3 caching, uses Claude AI to identify P&L accounts, and generates a comprehensive
          Excel workbook with 4 tabs: TB Dump, RE Summary, Journal Entry, and P&L Balances.
        </p>
      </div>

      {/* Error Display */}
      {error && (
        <div className="alert alert-error" style={{ marginBottom: '1rem' }}>
          {error}
          <button
            onClick={() => setError(null)}
            style={{
              marginLeft: '1rem',
              padding: '0.25rem 0.75rem',
              fontSize: '0.875rem',
            }}
            className="btn btn-secondary"
          >
            Dismiss
          </button>
        </div>
      )}

      {/* Actions Section */}
      <div className="actions-section" style={{ marginBottom: '1.5rem' }}>
        <button
          onClick={() => setShowCalculateModal(true)}
          className="btn btn-primary"
          disabled={loading}
        >
          <span style={{ marginRight: '0.5rem' }}>📊</span>
          Calculate New Adjustment
        </button>

        <button
          onClick={fetchCalculations}
          className="btn btn-secondary"
          disabled={loading}
          style={{ marginLeft: '1rem' }}
        >
          <span style={{ marginRight: '0.5rem' }}>🔄</span>
          Refresh
        </button>
      </div>

      {/* Calculation History Section */}
      <div className="history-section">
        <h3 style={{ marginBottom: '1rem', fontSize: '1.1rem', color: '#374151' }}>
          Calculation History
        </h3>

        {loading && (
          <div style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>
            <p>Loading calculations...</p>
          </div>
        )}

        {!loading && calculations.length === 0 && (
          <div className="empty-state" style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>
            <p>No retained earnings calculations found for this entity.</p>
            <p style={{ fontSize: '0.9rem' }}>Click "Calculate New Adjustment" to create one.</p>
          </div>
        )}

        {!loading && calculations.length > 0 && (
          <div className="table-container" style={{ overflowX: 'auto' }}>
            <table className="data-table">
              <thead>
                <tr>
                  <th>Timestamp</th>
                  <th>Fiscal Year</th>
                  <th>Stub Period</th>
                  <th>P&L Accounts</th>
                  <th>Adjustment Value</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {calculations.map((calc) => (
                  <tr key={calc.timestamp}>
                    <td>
                      <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                        {calc.timestamp}
                      </div>
                      {calc.upload_time && (
                        <div style={{ fontSize: '0.75rem', color: '#9ca3af' }}>
                          {formatDate(calc.upload_time)}
                        </div>
                      )}
                    </td>
                    <td>
                      <div style={{ fontSize: '0.875rem' }}>
                        {formatDate(calc.fiscal_year_start || calc.period1_start)} to
                      </div>
                      <div style={{ fontSize: '0.875rem', fontWeight: '500' }}>
                        {formatDate(calc.fiscal_year_end || calc.period1_end)}
                      </div>
                    </td>
                    <td>
                      <div style={{ fontSize: '0.875rem' }}>
                        {formatDate(calc.stub_period_start || calc.period2_start)} to
                      </div>
                      <div style={{ fontSize: '0.875rem', fontWeight: '500' }}>
                        {formatDate(calc.stub_period_end || calc.period2_end)}
                      </div>
                    </td>
                    <td style={{ textAlign: 'center', fontSize: '0.875rem' }}>
                      {calc.pl_accounts_count || calc.total_accounts || 'N/A'}
                    </td>
                    <td
                      style={{
                        textAlign: 'right',
                        fontWeight: '600',
                        color: '#059669',
                        fontFamily: 'monospace',
                      }}
                    >
                      {formatNumber(calc.adjustment_value)}
                    </td>
                    <td style={{ fontSize: '0.875rem' }}>
                      <span
                        style={{
                          padding: '0.25rem 0.5rem',
                          borderRadius: '0.25rem',
                          backgroundColor: calc.status === 'calculated' ? '#d1fae5' : '#fef3c7',
                          color: calc.status === 'calculated' ? '#065f46' : '#92400e',
                          fontSize: '0.75rem',
                          fontWeight: '500',
                        }}
                      >
                        {calc.status || 'calculated'}
                      </span>
                    </td>
                    <td>
                      <button
                        onClick={() => handleDownload(calc.fiscal_year_end, calc.timestamp)}
                        className="btn btn-sm btn-primary"
                        disabled={downloadingTimestamp === calc.timestamp}
                        title="Download Excel workbook"
                      >
                        {downloadingTimestamp === calc.timestamp ? '⏳' : '📥'}
                        {downloadingTimestamp === calc.timestamp ? ' Downloading...' : ' Download'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Calculation Summary */}
      {!loading && calculations.length > 0 && (
        <div style={{ marginTop: '1rem', fontSize: '0.875rem', color: '#6b7280' }}>
          Total calculations: {calculations.length}
        </div>
      )}

      {/* Calculate Modal */}
      {showCalculateModal && (
        <CalculateREModal
          entity={entity}
          onClose={() => setShowCalculateModal(false)}
          onComplete={handleCalculateComplete}
        />
      )}
    </div>
  );
}

export default RetainedEarningsPanel;
